import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  LayoutGrid, Fingerprint, Users, Building2, Calculator, Sparkles, LifeBuoy,
  ChevronDown, Lock, Check, HardHat,
} from "lucide-react";
import { PROGERE_ECOSYSTEM, CURRENT_MODULE_CODE } from "@/config/ecosystem";

const ICONS = { LayoutGrid, Fingerprint, Users, Building2, Calculator, Sparkles, LifeBuoy };

function statusFor(mod, access, lang) {
  const L = (fr, en) => (lang === "en" ? en : fr);
  if (mod.code === "core") return { label: L("ACTUEL", "CURRENT"), tone: "gold" };
  if (mod.code === "support") return { label: L("GRATUIT", "FREE"), tone: "green" };
  if (!access) return { label: L("DÉCOUVRIR", "DISCOVER"), tone: "muted", locked: true };
  if (access.construction) return { label: L("BIENTÔT", "SOON"), tone: "amber", soon: true };
  if (access.has_access) return { label: L("INCLUS", "INCLUDED"), tone: "green" };
  return { label: L("DÉCOUVRIR", "DISCOVER"), tone: "muted", locked: true };
}

const TONE = {
  gold: "text-[#EAB308] bg-[#EAB308]/15 border-[#EAB308]/35",
  green: "text-[#22c55e] bg-[#22c55e]/15 border-[#22c55e]/30",
  amber: "text-[#f59e0b] bg-[#f59e0b]/12 border-[#f59e0b]/30",
  muted: "text-white/45 bg-white/5 border-white/12",
};

export function EcosystemMenu() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const L = (fr, en) => (lang === "en" ? en : fr);

  useEffect(() => {
    if (open) {
      api.get("/dashboard/modules").then(({ data }) => setModules(data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    place();
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !btnRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  const accessByKey = Object.fromEntries(modules.map((m) => [m.key, m]));
  const ordered = [...PROGERE_ECOSYSTEM].sort((a, b) => a.sortOrder - b.sortOrder);
  const current = ordered.find((m) => m.code === CURRENT_MODULE_CODE);
  const others = ordered.filter((m) => m.code !== CURRENT_MODULE_CODE);

  const openModule = (mod) => {
    setOpen(false);
    const st = statusFor(mod, accessByKey[mod.code], lang);
    if (mod.route) { navigate(mod.route); return; }
    if (st.soon) { navigate(`/tarification?module=${mod.code}`); return; }
    if (st.locked) { navigate(`/tarification?module=${mod.code}`); return; }
    // Module inclus -> ouverture via la page modules (SSO auto par ?open=)
    navigate(`/app/modules?open=${mod.code}`);
  };

  const CurrentIcon = ICONS[current?.icon] || LayoutGrid;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        data-state={open ? "open" : "closed"}
        data-testid="ecosystem-menu-trigger"
        aria-label={L("Applications PROGÈRE", "PROGÈRE Apps")}
        className={`flex h-10 items-center gap-2 rounded-md border px-3 text-[13px] font-semibold transition-colors ${
          open
            ? "border-[#EAB308] bg-white/5 text-white shadow-[0_0_0_1px_rgba(234,179,8,0.12),0_0_18px_rgba(234,179,8,0.10)]"
            : "border-white/12 bg-white/5 text-white/80 hover:text-white hover:border-[#EAB308]/50"
        }`}
      >
        <LayoutGrid className="w-[18px] h-[18px]" />
        <span className="hidden sm:block">{L("Applications PROGÈRE", "PROGÈRE Apps")}</span>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          data-testid="ecosystem-menu-panel"
          style={{ position: "fixed", top: pos.top, right: pos.right, width: "min(390px, calc(100vw - 24px))", maxHeight: 620, transformOrigin: "top right" }}
          className="z-[9999] overflow-hidden rounded-[10px] border border-white/15 bg-[#0f1116] shadow-[0_24px_60px_rgba(0,0,0,0.62)] animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {/* En-tête */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
            <div>
              <div className="text-lg font-black leading-none">
                <span className="pg-pro">PRO</span><span className="pg-gere">GÈRE</span>
              </div>
              <div className="mt-1.5 text-[8px] font-extrabold uppercase tracking-[0.26em] text-white/40">
                {L("Votre écosystème d'entreprise", "Your business ecosystem")}
              </div>
            </div>
            <div className="text-right max-w-[130px]">
              <div className="text-xs font-semibold text-white/80 truncate">{user?.company_name}</div>
              <div className="inline-flex items-center gap-1 text-[10px] text-[#22c55e]"><span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> {L("Connecté", "Online")}</div>
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 540 }}>
            {/* Module actuel */}
            <div className="px-4 pt-3 pb-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-white/35">{L("Module actuel", "Current module")}</div>
            <div className="mx-3 mb-3 grid grid-cols-[42px_1fr_auto] items-center gap-3 rounded-[9px] border border-[#EAB308]/40 bg-gradient-to-br from-[#EAB308]/12 to-transparent px-3 py-2.5" data-testid="ecosystem-current">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#EAB308]/40 bg-[#EAB308]/10">
                <CurrentIcon className="w-5 h-5 text-[#EAB308]" />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-black text-[#EAB308] truncate">{current?.fullName}</div>
                <div className="text-[11px] text-white/55 truncate">{current?.description}</div>
              </div>
              <span className="rounded px-2 py-1 text-[8px] font-black tracking-wider text-black bg-[#EAB308]">{L("ACTIF", "ACTIVE")}</span>
            </div>

            {/* Autres applications */}
            <div className="px-4 pb-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-white/35">{L("Vos applications", "Your apps")}</div>
            <div className="grid grid-cols-2 gap-2 px-3 pb-3">
              {others.map((mod) => {
                const Icon = ICONS[mod.icon] || LayoutGrid;
                const st = statusFor(mod, accessByKey[mod.code], lang);
                const accent = mod.accent === "gold" ? "#EAB308" : "#dc143c";
                return (
                  <button
                    key={mod.code}
                    onClick={() => openModule(mod)}
                    data-testid={`ecosystem-module-${mod.code}`}
                    className={`relative min-h-[92px] rounded-lg border border-white/10 bg-[#15161b] p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#EAB308]/40 hover:bg-[#1c1d23] ${st.locked ? "opacity-75" : ""}`}
                  >
                    {st.locked && <Lock className="absolute top-2.5 right-2.5 w-3.5 h-3.5 text-[#EAB308]" />}
                    {st.tone === "green" && !st.locked && <Check className="absolute top-2.5 right-2.5 w-3.5 h-3.5 text-[#22c55e]" />}
                    {st.soon && <HardHat className="absolute top-2.5 right-2.5 w-3.5 h-3.5 text-[#f59e0b]" />}
                    <div className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: `${accent}44`, background: `${accent}12` }}>
                      <Icon className="w-4 h-4" style={{ color: accent }} />
                    </div>
                    <div className="mt-2 text-[11px] font-black uppercase text-white">{mod.name}</div>
                    <div className="mt-0.5 text-[9px] leading-tight text-white/45">{mod.description}</div>
                    <span className={`mt-2 inline-block rounded border px-1.5 py-0.5 text-[8px] font-bold tracking-wide ${TONE[st.tone]}`}>{st.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
